import { useEffect, useMemo, useState } from "react";
import { AddressResponse } from "../../../../core/api/addresses/types";
import Table from "../../../../ui-lib/components/Tables/Table";
import { CustomTableCell } from "../CustomTableCell/CustomTableCell";
import EditAddressModal from "./EditAddressModal";
import { DeleteAddressModal } from "./DeleteAddressModal";
import useTranslations from "../../../../core/i18n/useTranslations";
import { useModalIdHook } from "../../../../core/hooks/modalIdsHandlers/useIdModalHook";
import { useHistory } from "react-router";
import { urlSearchParser } from "../../../../ui-lib/utils/urlSearchParser";
import useUser from "../../../../core/user/useUser";

export const AddressTable = ({
  objectId,
  addresses,
  onAddressUpdate,
}: {
  objectId: string;
  addresses: AddressResponse[];
  onAddressUpdate: () => void;
}) => {
  const t = useTranslations();
  const { config } = useUser();
  const history = useHistory();

  const tableState = history.location.search;
  const searchState = urlSearchParser(tableState);
  const hasTrygghetsjourenJourledRole = useMemo(
    () => config?.show.includes("ShowObjectDetailsVariant1"),
    []
  );

  const [addressId, setAddressId, resetAddressId] = useModalIdHook(
    searchState?.addressId,
    "addressId"
  );

  const [upForEdit, setUpForEdit] = useState<number | undefined>();
  const [upForDelete, setUpForDelete] = useState<number | undefined>();

  useEffect(() => {
    if (addressId.id) {
      setUpForEdit(+addressId.id);
    }
  }, [addressId]);

  return (
    <>
      <Table<AddressResponse>
        columns={[
          {
            header: t("Table:Addresses_Description"),
            fieldTemplate: (rowData) => (
              <CustomTableCell
                label={rowData.description}
                placeholder={t("Objects:placeholder_address_no_description")}
              />
            ),
          },
          {
            header: t("Table:Street"),
            fieldTemplate: (rowData) => {
              const allDataPresent = rowData.streetAddress && rowData.city;
              const label = `${rowData.streetAddress || ""}${
                allDataPresent ? ", " : ""
              }${rowData.city ? rowData.city : ""}`;

              return <CustomTableCell label={label} icon="location-marker" />;
            },
          },
          {
            header: t("Common:address_main_number"),
            fieldTemplate: (rowData) => (
              <CustomTableCell
                label={rowData.telephone1}
                icon="phone"
                placeholder={t("Objects:placeholder_address_no_phone")}
              />
            ),
          },
        ]}
        items={addresses}
        rowActions={[
          ...(!hasTrygghetsjourenJourledRole
            ? [
                {
                  text: t("Objects:edit_details"),
                  icon: "pencil-alt",
                  iconVariant: "secondary" as const,
                  iconSize: 16,
                  onClick: (rowData: AddressResponse) => {
                    setAddressId(rowData.id);
                  },
                },
              ]
            : []),
          {
            text: t("Objects:remove_from"),
            icon: "x",
            iconVariant: "secondary",
            iconSize: 16,
            onClick: (rowData) => {
              setUpForDelete(rowData.id);
            },
          },
        ]}
        rowActionsColWidth={45}
        rowActionsFixed
        hideEmptyMessage
        noRowsMessage={t("Table:no_records_found")}
        showRowHover
        rowActionContextMenu
        withShowMore
      />
      {!!upForEdit && (
        <EditAddressModal
          objectId={objectId}
          addressId={upForEdit}
          onClose={() => {
            setUpForEdit(undefined);
            resetAddressId();
          }}
          onEdit={() => {
            setUpForEdit(undefined);
            resetAddressId();
            onAddressUpdate();
          }}
        />
      )}
      <DeleteAddressModal
        objectId={objectId}
        addressId={upForDelete}
        onClose={() => setUpForDelete(undefined)}
        onDelete={() => {
          setUpForDelete(undefined);
          onAddressUpdate();
        }}
      />
    </>
  );
};
